import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom'; // Importar useNavigate
import AdminLayout from '../components/layout/AdminLayout';
import { IProject } from '../types/Project';
import { getProjects, deleteProject as apiDeleteProject, toggleProjectVisibility, toggleProjectFeatured } from '../services/ProjectService';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';
import styles from '../styles/Dashboard.module.css'; 
import { PencilSquareIcon, TrashIcon, EyeIcon, EyeSlashIcon, StarIcon } from '@heroicons/react/24/outline';
import { useToast } from '../hooks/useToast';

const ProjectsPage: React.FC = () => {
    const [projects, setProjects] = useState<IProject[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const toast = useToast();
    const navigate = useNavigate(); // Para navegación manual
    const { isOpen, config, confirm, handleConfirm, handleCancel } = useConfirm();

    const fetchProjects = async () => {
        setLoading(true);
        setError(null);
        try {
            const fetchedProjects = await getProjects();
            setProjects(fetchedProjects);
        } catch (err: any) {
            setError(err.message || "Fallo al cargar proyectos desde el servidor.");
            toast.error('Error', 'No se pudieron cargar los proyectos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    // --- Lógica de Permisos ---
    const userRole = localStorage.getItem('userRole') || 'observador';
    const canEdit = userRole === 'administrador' || userRole === 'editor';
    const canDelete = userRole === 'administrador';

    // Función auxiliar para verificar y notificar
    const checkPermission = (type: 'edit' | 'delete') => {
        if (type === 'edit' && !canEdit) {
            toast.warning('Acceso Denegado', 'Solo administradores y editores pueden editar.');
            return false;
        }
        if (type === 'delete' && !canDelete) {
            toast.warning('Acceso Denegado', 'Solo administradores pueden eliminar.');
            return false;
        }
        return true;
    };

    // --- Handlers Modificados ---

    const handleCreate = (e: React.MouseEvent) => {
        e.preventDefault();
        if (checkPermission('edit')) {
            navigate('/projects/new');
        }
    };

    const handleEditClick = (e: React.MouseEvent, id: number) => {
        e.preventDefault();
        if (checkPermission('edit')) {
            navigate(`/projects/edit/${id}`);
        }
    };

    const handleDelete = async (id: number, title: string) => {
        if (!checkPermission('delete')) return; // Validación

        const confirmed = await confirm({
            title: 'Eliminar Proyecto',
            message: `¿Estás seguro de eliminar el proyecto: "${title}"? Esta acción no se puede deshacer.`,
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            type: 'danger'
        });

        if (confirmed) {
            try {
                const success = await apiDeleteProject(id);
                if (success) {
                    fetchProjects();
                    toast.success('Éxito', `Proyecto "${title}" eliminado con éxito.`);
                } else {
                    toast.error('Error', 'Error al eliminar el proyecto.');
                }
            } catch (err: any) {
                toast.error('Error', `Error al eliminar: ${err.message}`);
            }
        }
    };

    const handleToggleVisibility = async (id: number, currentState: boolean) => {
        if (!checkPermission('edit')) return; // Validación

        try {
            await toggleProjectVisibility(id, !currentState);
            setProjects(prev => prev.map(project =>
                project.id === id ? { ...project, isHidden: !currentState } : project
            ));
            toast.info('Visibilidad actualizada', `Proyecto ${!currentState ? 'visible' : 'oculto'}`);
        } catch (err: any) {
            toast.error('Error', `Error al cambiar visibilidad: ${err.message}`);
            fetchProjects();
        }
    };

    const handleToggleFeatured = async (id: number, currentState: boolean) => {
        if (!checkPermission('edit')) return; // Validación

        try {
            await toggleProjectFeatured(id, !currentState);
            setProjects(prev => prev.map(project =>
                project.id === id ? { ...project, isFeatured: !currentState } : project
            ));
            toast.info('Destacado actualizado', `Proyecto ${!currentState ? 'destacado' : 'sin destacar'}`);
        } catch (err: any) {
            toast.error('Error', `Error al cambiar estado de destacado: ${err.message}`);
            fetchProjects();
        }
    };

    if (loading) return <AdminLayout pageTitle="Gestión de Proyectos"><p>Cargando proyectos...</p></AdminLayout>;
    if (error) return <AdminLayout pageTitle="Gestión de Proyectos"><p style={{ color: 'red', textAlign: 'center' }}>Error: {error}</p></AdminLayout>;

    return (
        <AdminLayout pageTitle="Gestión de Proyectos">
            <div className={styles.mainContentHeader}>
                <h2 className={styles.headerTitle}>Proyectos Publicados</h2>
                {/* Usamos onClick en lugar de Link directo para validar */}
                <button onClick={handleCreate} className={styles.primaryButton}>
                    + Nuevo Proyecto
                </button>
            </div>

            <div className={styles.recentActivity}>
                <table className={styles.projectTable}>
                    <thead>
                        <tr>
                            <th>Título</th>
                            <th>Categoría</th>
                            <th>Fase</th>
                            <th>Destacado</th>
                            <th>Visibilidad</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {projects.map((project) => (
                            <tr key={project.id}>
                                <td>{project.title}</td>
                                <td>{project.category}</td>
                                <td>{project.fases}</td>
                                <td className={styles.featureCell}>
                                    <button 
                                        onClick={() => handleToggleFeatured(project.id, project.isFeatured)}
                                        title={project.isFeatured ? "Quitar de Destacados" : "Marcar como Destacado"}
                                        className={`${styles.iconButton} ${project.isFeatured ? styles.highlighted : ''}`}
                                    >
                                        <StarIcon className={styles.iconButtonIcon} />
                                    </button>
                                </td>
                                <td className={styles.visibilityCell}>
                                    <button
                                        onClick={() => handleToggleVisibility(project.id, project.isHidden)}
                                        title={project.isHidden ? "Mostrar en Web" : "Ocultar de Web"}
                                        className={`${styles.iconButton} ${project.isHidden ? styles.hidden : ''}`}
                                    >
                                        {project.isHidden ? <EyeSlashIcon className={styles.iconButtonIcon} /> : <EyeIcon className={styles.iconButtonIcon} />}
                                    </button>
                                </td>
                                <td>
                                    <div className={styles.actionsCell}>
                                        <button 
                                            onClick={(e) => handleEditClick(e, project.id)} 
                                            title="Editar" 
                                            className={styles.editButton}
                                        >
                                            <PencilSquareIcon className={styles.iconButtonIcon} />
                                        </button>
                                        
                                        <button 
                                            onClick={() => handleDelete(project.id, project.title)} 
                                            title="Eliminar" 
                                            className={styles.deleteButton}
                                        >
                                            <TrashIcon className={styles.iconButtonIcon} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {projects.length === 0 && <p className={styles.noProjects}>No hay proyectos registrados.</p>}
            </div>

            <ConfirmModal
                isOpen={isOpen}
                title={config?.title || ''}
                message={config?.message || ''}
                confirmText={config?.confirmText}
                cancelText={config?.cancelText}
                type={config?.type}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
        </AdminLayout>
    );
};

export default ProjectsPage;